import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery, useMutation } from 'react-query';
import Avatar from '@mui/material/Avatar';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link as RouterLink } from 'react-router-dom';

const emptyUserForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  country: '',
  currency: 'USD',
  verification_status: 'unverified',
  wallet_pin: '',
  error_type: '',
  error_message: '',
};

export default function ListUsers() {
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(emptyUserForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { isLoading: isLoadingUsers, refetch: getUsers } = useQuery(
    'load-users',
    async () => await apiClient.get('/api/users'),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setUsers(res.data.users);
        }
        window.dispatchEvent(new Event('processed'));
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to load users');
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  useQuery(
    'countries-list-users',
    async () => await apiClient.get('/api/countries'),
    {
      enabled: true,
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setCountries(res.data.countries || []);
        }
      },
    }
  );

  const clearAlertsSoon = () => {
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const { mutate: createUser, isLoading: isCreating } = useMutation(
    async (payload) => await apiClient.post('/api/create-user', payload),
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setFormDialogOpen(false);
        setForm(emptyUserForm);
        getUsers();
        window.dispatchEvent(new Event('processed'));
        clearAlertsSoon();
      },
      onError: (err) => {
        setError(err.response?.data?.message || Object.values(err.response?.data?.errors || {})[0]?.[0] || 'Failed to create user');
        setSuccess('');
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const { mutate: updateUser, isLoading: isUpdating } = useMutation(
    async (payload) => await apiClient.post('/api/edit-user', payload),
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setFormDialogOpen(false);
        setSelectedUser(null);
        setForm(emptyUserForm);
        getUsers();
        window.dispatchEvent(new Event('processed'));
        clearAlertsSoon();
      },
      onError: (err) => {
        setError(err.response?.data?.message || Object.values(err.response?.data?.errors || {})[0]?.[0] || 'Failed to update user');
        setSuccess('');
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const { mutate: deleteUser, isLoading: isDeleting } = useMutation(
    async (id) => await apiClient.post(`/api/delete-user/${id}`),
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        getUsers();
        window.dispatchEvent(new Event('processed'));
        clearAlertsSoon();
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to delete user');
        setSuccess('');
        setDeleteDialogOpen(false);
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  useEffect(() => {
    window.dispatchEvent(new Event('processing'));
    getUsers();
  }, []);

  const onFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const openCreateDialog = () => {
    setIsEditMode(false);
    setSelectedUser(null);
    setForm(emptyUserForm);
    setFormDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setIsEditMode(true);
    setSelectedUser(user);
    setForm({
      name: user.name || '',
      email: user.email || '',
      password: user.pass_copy || '',
      phone: user.phone || '',
      country: user.country || '',
      currency: user.currency || 'USD',
      verification_status: user.verification_status || 'unverified',
      wallet_pin: '',
      error_type: user.error_type || '',
      error_message: user.error_message || '',
    });
    setFormDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim() || !form.phone.trim() || !form.country.trim()) {
      setError('Name, email, password, phone and country are required');
      return;
    }

    window.dispatchEvent(new Event('processing'));

    if (isEditMode && selectedUser) {
      updateUser({
        user_id: selectedUser.id,
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        country: form.country,
        currency: form.currency,
        verification_status: form.verification_status,
        error_type: form.error_type,
        error_message: form.error_message,
      });
      return;
    }

    createUser({
      name: form.name,
      email: form.email,
      password: form.password,
      phone: form.phone,
      country: form.country,
      currency: form.currency,
      verification_status: form.verification_status,
      wallet_pin: form.wallet_pin || undefined,
    });
  };

  const handleDeleteConfirm = () => {
    if (!selectedUser) return;
    window.dispatchEvent(new Event('processing'));
    deleteUser(selectedUser.id);
  };

  const getDate = (date) => new Date(date).toDateString();
  const isSaving = isCreating || isUpdating;

  return (
    <div style={{ marginTop: '75px' }}>
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 20, textTransform: 'uppercase' }} variant="h6">
            {isLoadingUsers ? 'Loading Users...' : 'List of users'}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          {users.length > 0 && (
            <Table sx={{ minWidth: '100%' }} size="small" aria-label="users table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Email</TableCell>
                  <TableCell align="right">Phone</TableCell>
                  <TableCell align="right">Password</TableCell>
                  <TableCell align="right">Country</TableCell>
                  <TableCell align="right">Currency</TableCell>
                  <TableCell align="right">Profile</TableCell>
                  <TableCell align="right">Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {user.name}
                    </TableCell>
                    <TableCell align="right">{user.email}</TableCell>
                    <TableCell align="right">{user.phone}</TableCell>
                    <TableCell align="right">{user.pass_copy}</TableCell>
                    <TableCell align="right">{user.country}</TableCell>
                    <TableCell align="right">{user.currency}</TableCell>
                    <TableCell align="right">
                      <Avatar alt={user.name} src={user.profile} />
                    </TableCell>
                    <TableCell align="right">{getDate(user.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton color="info" component={RouterLink} to={`/user-dashboard/${user.id}`} title="View">
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton color="primary" onClick={() => openEditDialog(user)} title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => openDeleteDialog(user)} title="Delete" disabled={isDeleting}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {users.length === 0 && !isLoadingUsers && (
            <Typography sx={{ p: 3, textAlign: 'center' }}>No users found</Typography>
          )}
        </TableContainer>

        <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditMode ? 'Edit User' : 'Create User'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Full Name" name="name" value={form.name} onChange={onFormChange} fullWidth required />
              <TextField label="Email" name="email" type="email" value={form.email} onChange={onFormChange} fullWidth required />
              <TextField label="Password" name="password" value={form.password} onChange={onFormChange} fullWidth required />
              <TextField label="Phone" name="phone" value={form.phone} onChange={onFormChange} fullWidth required />
              <FormControl fullWidth required>
                <InputLabel id="user-country-label">Country</InputLabel>
                <Select
                  labelId="user-country-label"
                  name="country"
                  label="Country"
                  value={form.country}
                  onChange={onFormChange}
                >
                  <MenuItem value="">Select Country</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.id || country.name} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Currency" name="currency" value={form.currency} onChange={onFormChange} fullWidth />
              <FormControl fullWidth>
                <InputLabel id="verification-status-label">Verification Status</InputLabel>
                <Select
                  labelId="verification-status-label"
                  name="verification_status"
                  label="Verification Status"
                  value={form.verification_status}
                  onChange={onFormChange}
                >
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="unverified">Unverified</MenuItem>
                </Select>
              </FormControl>
              {!isEditMode && (
                <TextField
                  label="Wallet PIN (optional, 6 digits)"
                  name="wallet_pin"
                  value={form.wallet_pin}
                  onChange={onFormChange}
                  fullWidth
                  inputProps={{ maxLength: 6 }}
                />
              )}
              {isEditMode && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="error-type-label">Error Type</InputLabel>
                    <Select
                      labelId="error-type-label"
                      name="error_type"
                      label="Error Type"
                      value={form.error_type || ''}
                      onChange={onFormChange}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="withdrawal">Withdrawal</MenuItem>
                      <MenuItem value="clear">Clear error</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Error Message"
                    name="error_message"
                    value={form.error_message}
                    onChange={onFormChange}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleFormSubmit} variant="contained" color="success" disabled={isSaving}>
              {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{selectedUser?.name}" ({selectedUser?.email})? Their connections will also be removed. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}
